import { useEffect, useState, useRef, useCallback } from "react";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../../assets/table-style";
import swal from "sweetalert";
import { format } from "date-fns";
import BASE_URL from "../../../../assets/global/url";
import { useNavigate } from "react-router-dom";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { useServerSort } from "../../../../hooks/customHook/sortingHook/useTableSort";
import axios from "axios";
import FilterStore from "../../../../hooks/customHook/FilterStore";
import { formatCurrency } from "../../../../utils/numberFormatter";
import { runBulkApproval } from "../../../../utils/bulkApproval";
import { useBulkSelection } from "../../../../hooks/customHook/useBulkSelection";
import BulkActionBar from "../../../../components/BulkActionBar";

const Pending_tab = ({
  authrztn,
  currencyId,
  userLoggedID,
  selectedCutOff,
  fetchCutOff = () => {},
  status_tab,
  clearFilterSignal = 0,
}) => {
  const navigate = useNavigate();
  const debounceTimer = useRef(null);
  const pageRestored = useRef(false);

  // Pick E1 (For-Approval) or E2 (Approved) methods based on status_tab
  const FS =
    status_tab === "For-Approval"
      ? {
          getSearch: () => FilterStore.E1getSearch(),
          saveSearch: (v) => FilterStore.E1saveSearch(v),
          getFilterColumn: () => FilterStore.E1getFilterColumn(),
          saveFilterColumn: (v) => FilterStore.E1saveFilterColumn(v),
          getPage: () => FilterStore.E1getPage(),
          savePage: (v) => FilterStore.E1savePage(v),
          getPerPage: () => FilterStore.E1getPerPage(),
          savePerPage: (v) => FilterStore.E1savePerPage(v),
        }
      : {
          getSearch: () => FilterStore.E2getSearch(),
          saveSearch: (v) => FilterStore.E2saveSearch(v),
          getFilterColumn: () => FilterStore.E2getFilterColumn(),
          saveFilterColumn: (v) => FilterStore.E2saveFilterColumn(v),
          getPage: () => FilterStore.E2getPage(),
          savePage: (v) => FilterStore.E2savePage(v),
          getPerPage: () => FilterStore.E2getPerPage(),
          savePerPage: (v) => FilterStore.E2savePerPage(v),
        };

  const [searchText, setSearchText] = useState(() => FS.getSearch());
  const [filterColumn, setFilterColumn] = useState(() => FS.getFilterColumn());

  useEffect(() => {
    FS.saveSearch(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    FS.saveFilterColumn(filterColumn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterColumn]);

  // Bulk approve/reject is only offered on the pending tab to approvers
  const canBulkApprove =
    status_tab === "For-Approval" && authrztn.includes("Expenses-Approved");

  const pagination = useServerPagination("about:blank", FS.getPerPage());

  const bulk = useBulkSelection(pagination.data);

  // No initial sortBy: the backend default (latest updated first) has no visible column,
  // so every header starts neutral until the user picks one
  const sort = useServerSort(pagination);

  const fetchExpensesData = useCallback(
    (currentSearchText = searchText, currentColumn = filterColumn) => {
      // if (!selectedCutOff?.from || !selectedCutOff?.to) return;
      // The Pending tab always shows ALL pending expenses — the date range
      // picker is hidden on that tab, so it always falls through to the
      // show-all branch below (even if the Approved tab saved a range).
      // The Approved tab keeps using the selected date range.
      const isPendingTab = status_tab === "For-Approval";
      const hasDateRange =
        selectedCutOff?.from && selectedCutOff?.to && !isPendingTab;
      const trimmed = currentSearchText.trim();
      // if (!currentSearchText.trim()) {
      if (trimmed) {
        // Search mode — use search route, no date restriction
        pagination.updateApiUrl(`${BASE_URL}/expenses/getExpensesData-search`);
        pagination.updateParams({
          startDate: null,
          endDate: null,
          currencyId,
          searchText: currentSearchText,
          filterColumn: currentColumn,
          status_tab,
          userLoggedID,
        });
      } else if (hasDateRange) {
        // Normal date-filter mode
        pagination.updateApiUrl(`${BASE_URL}/expenses/getExpensesData`);
        pagination.updateParams({
          startDate: selectedCutOff.from,
          endDate: selectedCutOff.to,
          currencyId,
          status_tab,
          userLoggedID,
        });
      } else {
        // Show-all mode (Clear Filter) — no search, no dates → return all records
        pagination.updateApiUrl(`${BASE_URL}/expenses/getExpensesData-search`);
        pagination.updateParams({
          startDate: null,
          endDate: null,
          currencyId,
          // searchText: currentSearchText,
          // filterColumn: currentColumn,
          searchText: "",
          filterColumn: "all",
          status_tab,
          userLoggedID,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedCutOff?.from,
      selectedCutOff?.to,
      currencyId,
      status_tab,
      userLoggedID,
    ],
  );

  useEffect(() => {
    fetchExpensesData();
  }, [fetchExpensesData]);

  // --- Clear Filter signal from parent ---
  // When the parent fires handleClearFilter it increments clearFilterSignal.
  // Reset search text and filter column; explicit refetch overrides any stale
  // in-flight call that may have fired from the selectedCutOff change.
  useEffect(() => {
    if (clearFilterSignal === 0) return;
    setSearchText("");
    FS.saveSearch("");
    setFilterColumn("all");
    FS.saveFilterColumn("all");
    fetchExpensesData("", "all"); // force show-all fetch with clean state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFilterSignal]);

  // --- Page / PerPage retention ---
  useEffect(() => {
    if (!pageRestored.current) return;
    if (pagination.currentPage) {
      FS.savePage(pagination.currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage]);

  useEffect(() => {
    if (pagination.itemsPerPage) {
      FS.savePerPage(pagination.itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    const savedPage = FS.getPage();
    if (savedPage > 1) {
      setTimeout(() => {
        pagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 600);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchExpensesData(value, filterColumn);
    }, 500);
  };

  const handleFilterColumnChange = (value) => {
    setFilterColumn(value);
    setSearchText("");
    fetchExpensesData("", value);
  };

  const handleDeleteExpenses = async (expensesId, expensesDate) => {
    swal({
      title: "Confirm Deletion",
      text: "Are you sure you want to delete?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await axios.delete(
            `${BASE_URL}/expenses/deleteExpenses/${expensesId}/${expensesDate}`,
            { data: { userLoggedID } },
          );

          if (response.status === 200) {
            swal({
              title: "Expenses Deleted Successfully!",
              text: "The expenses has been successfully deleted.",
              icon: "success",
              button: "OK",
            }).then(() => {
              fetchExpensesData();
              fetchCutOff();
            });
          } else if (response.status === 203) {
            const { transactionNumber, moduleType } = response.data;
            const swalInfo = document.createElement("div");
            swalInfo.innerHTML = `
              <div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>
              <div style="text-align: center; margin-top: 10px;">
                Delete first the expenses in module <strong>${moduleType}</strong> with 
                Transaction Number: <strong>${transactionNumber}</strong>
              </div>`;
            swal({ icon: "error", content: swalInfo });
          } else if (response.status === 202) {
            const { expensesDate: resDate, CutoffName } = response.data;
            swal({
              title: "Delete Prohibited!",
              text: `Expenses cannot be deleted as its expenses date (${resDate}) falls within the posted cutoff period named "${CutoffName}".`,
              icon: "warning",
              button: "OK",
            });
          } else {
            swal({
              icon: "error",
              title: "Something went wrong",
              text: "Please contact our support team.",
            });
          }
        } catch (error) {
          if (error.response.status === 401) {
            swal({
              icon: "error",
              // title: "Please contact your support",
              title: error.response.data.title,
              text: error.response.data.message,
            }); // test 1
          } else {
            if (error.response?.status === 300) {
              const { fixedAssetTransactionNumber, module } =
                error.response.data;
              const swalInfo = document.createElement("div");
              swalInfo.innerHTML = `
              <div style="font-size: 1.5rem; font-weight: bold;">Deletion Prohibited!</div>
              <div style="text-align: center; margin-top: 10px;">
                Delete first the expenses in module <strong>${module}</strong> with 
                Transaction Number: <strong>${fixedAssetTransactionNumber}</strong>
              </div>`;
              swal({ icon: "error", content: swalInfo });
            } else {
              swal({
                icon: "error",
                title: "Error",
                text: "An error occurred while deleting the expenses.",
              });
            }
          }
        }
      }
    });
  };

  const handleBulkAction = (action) =>
    runBulkApproval({
      action,
      rows: bulk.selectedRows,
      url: `${BASE_URL}/expenses/${action === "approve" ? "bulk-approved" : "bulk-rejected"}`,
      userLoggedID,
      getId: (row) => row.id,
      getAmount: (row) => row.totalAmount,
      getCurrencyName: (row) => row.currency?.currency_name,
      setIsProcessing: bulk.setIsProcessing,
      onDone: () => {
        fetchExpensesData();
        fetchCutOff();
      },
    });

  const handleRowClick = (data) => {
    navigate(`/accounting/view-expenses/${data.id}`);
  };

  const columns = [
    {
      name: "Transaction ID",
      selector: (row) => row.client_transaction_id,
      sortField: "client_transaction_id",
    },
    {
      name: "Expenses Type",
      selector: (row) =>
        row.expenses2_id === null
          ? "NA"
          : `${row.expenses2.sub_type} (${row.expenses2.expenses_one.expenses_type_one})`,
      sortField: "expenses_type",
    },
    {
      name: "Foreign Type",
      selector: (row) => (row.foreign === "" ? "NA" : row.foreign),
      sortField: "foreign",
    },
    {
      name: "Amount",
      selector: (row) =>
        formatCurrency(row.totalAmount, row.currency.currency_name),
      sortField: "totalAmount",
    },
    {
      name: "Description",
      selector: (row) => (row.desc === "" ? "NA" : row.desc),
      sortField: "desc",
    },
    {
      name: "Accountable",
      selector: (row) =>
        row.masterlist
          ? `${row.masterlist.fname} ${row.masterlist.mname ? row.masterlist.mname + " " : ""}${row.masterlist.lname}`
          : row.isExternalAccountable && row.externalAccountable
            ? row.externalAccountable
            : "N/A",
      sortField: "accountable",
    },
    {
      name: "Date Approved",
      selector: (row) =>
        row.date_approved
          ? format(row.date_approved, "MMM/dd/yyyy hh:mm a")
          : "n/a",
      sortField: "date_approved",
    },
    {
      name: "Expenses Date",
      selector: (row) => format(row.expenses_date, "MMM/dd/yyyy"),
      sortField: "expenses_date",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortField: "status",
      cell: (row) => {
        const color =
          row.status === "For-Approval"
            ? "#FFA500"
            : row.status === "Approved"
              ? "#3B9F3F"
              : row.status === "Rejected"
                ? "#FF0000"
                : "initial";
        return (
          <div
            style={{
              padding: "5px 10px",
              borderRadius: "5px",
              color,
              textTransform: "uppercase",
              fontWeight: "bold",
            }}>
            {row.status}
          </div>
        );
      },
    },
  ];

  if (authrztn.includes("Expenses-Delete")) {
    columns.push({
      name: "Action",
      selector: (row) => (
        <i
          className="fas fa-trash"
          style={{ cursor: "pointer", color: "red", fontSize: "1.5rem" }}
          onClick={() => handleDeleteExpenses(row.id, row.expenses_date)}></i>
      ),
    });
  }

  const searchFilters = [
    { value: "all", label: "All" },
    { value: "client_transaction_id", label: "Transaction ID" },
    {
      value: "expenses2.expenses_one.expenses_type_one",
      label: "Expenses Type 1",
    },
    { value: "expenses2.sub_type", label: "Expenses Type 2" },
    { value: "foreign", label: "Foreign Type" },
    { value: "desc", label: "Description" },
    { value: "expenses_date", label: "Expenses Date" },
    { value: "expensesCheque", label: "Cheque Number" },
  ];

  return (
    <div>
      <div className="w-100 mt-4 mb-2 container-fluid">
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={searchText}
            onChange={handleSearchChange}
            aria-label="Search"
          />
          <button
            type="button"
            className="btn btn-outline-secondary dropdown-toggle-split"
            data-bs-toggle="dropdown"
            aria-expanded="false">
            <i className="fa-solid fa-sliders"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            {searchFilters.map(({ value, label }, i) => (
              <li key={i}>
                <button
                  className={`dropdown-item ${filterColumn === value ? "active" : ""}`}
                  onClick={() => handleFilterColumnChange(value)}>
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="w-100 mt-3 container-fluid">
        {canBulkApprove && (
          <BulkActionBar
            selectedCount={bulk.selectedRows.length}
            isProcessing={bulk.isProcessing}
            onApprove={() => handleBulkAction("approve")}
            onReject={() => handleBulkAction("reject")}
          />
        )}
        <DataTable
          columns={sort.sortColumns(columns)}
          data={pagination.data}
          customStyles={customStyles}
          onRowClicked={handleRowClick}
          className="dataTable"
          {...(canBulkApprove && bulk.tableProps)}
          {...sort.sortProps}
        />
        <PaginationControls {...pagination} />
      </div>
    </div>
  );
};

export default Pending_tab;
